import { __assign, __extends, __rest } from "tslib";
import * as React from "react";
import { Animated, StyleSheet, View } from "react-native";
import color from "color";
import ActivityIndicator from "../ActivityIndicator";
import Icon from "../Icon";
import Surface from "../Surface";
import PnmacText from "../Typography/PnmacText";
import PnmacTouchableRipple from "../TouchableRipple";
import { black } from "../styles/colors";
import styles from "./PnmacUibutton.styles.js";
import Hoverable from "../Hoverable.js";
import configureFonts from "../styles/fonts";
import { withOpacity } from '../v2/withOpacity';
/**
 * A button is component that the user can press to trigger an action.

 * ## Usage
 * ```js
 * import * as React from 'react';
 * import { PnmacButton } from 'pnmac-universal-components';
 *
 * const MyComponent = () => (
 *   <PnmacButton icon="camera" mode="contained" onPress={() => console.log('Pressed')}>
 *     Press me
 *   </PnmacButton>
 * );
 *
 * export default MyComponent;
 * ```
 */
var PnmacUIButton = /** @class */function (_super) {
  __extends(PnmacUIButton, _super);
  function PnmacUIButton() {
    var _this = this;
    var _a;
    _this = _super.apply(this, arguments) || this;
    _this.state = {
      elevation: new Animated.Value(_this.props.mode === "contained" ? 2 : 0),
      originalBackGroundColor: ((_a = _this.props.style) === null || _a === void 0 ? void 0 : _a.backgroundColor) ? _this.props.style.backgroundColor : "#003087"
    };
    _this.handlePressIn = function () {
      if (_this.props.mode === "contained") {
        Animated.timing(_this.state.elevation, {
          toValue: 8,
          duration: 200 * 1.0,
          useNativeDriver: false
        }).start();
      }
    };
    _this.handlePressOut = function () {
      if (_this.props.mode === "contained") {
        Animated.timing(_this.state.elevation, {
          toValue: 2,
          duration: 150 * 1.0,
          useNativeDriver: false
        }).start();
      }
    };
    return _this;
  }
  PnmacUIButton.prototype.render = function () {
    var _this = this;
    var _a = this.props,
      disabled = _a.disabled,
      compacted = _a.compacted,
      mode = _a.mode,
      dark = _a.dark,
      loading = _a.loading,
      icon = _a.icon,
      buttonColor = _a.color,
      children = _a.children,
      uppercase = _a.uppercase,
      accessibilityLabel = _a.accessibilityLabel,
      onPress = _a.onPress,
      style = _a.style,
      theme = _a.theme,
      contentStyle = _a.contentStyle,
      labelStyle = _a.labelStyle,
      testID = _a.testID,
      iconSize = _a.iconSize,
      iconStyles = _a.iconStyles,
      buttonOutline = _a.buttonOutline,
      rest = __rest(_a, ["disabled", "compacted", "mode", "dark", "loading", "icon", "color", "children", "uppercase", "accessibilityLabel", "onPress", "style", "theme", "contentStyle", "labelStyle", "testID", "iconSize", "iconStyles", "buttonOutline"]);
    var backgroundColor, borderColor, textColor, borderWidth;
    if (mode === "contained") {
      if (disabled) {
        backgroundColor = withOpacity('#003087', 0.4);
        textColor = '#FFFFFF';
      } else if (buttonColor) {
        backgroundColor = buttonColor;
        textColor = buttonColor;
      } else {
        backgroundColor = "#003087";
        textColor = "#FFFFFF";
      }
    } else {
      backgroundColor = "transparent";
    }
    if (mode === "outlined") {
      if (disabled) {
        borderColor = withOpacity('#003087', 0.4);
        textColor = withOpacity('#003087', 0.4);
      } else {
        borderColor = "#003087";
        textColor = '#003087';
      }
      borderWidth = buttonOutline || 1;
    }
    var rippleColor = textColor ? withOpacity(textColor.slice(0, 7), 0.4) : undefined;
    if (this.state.hovering && !disabled) {
      if (mode === "outlined") {
        borderColor = withOpacity('#003087', 0.8);
        textColor = withOpacity('#003087', 0.8);
      } else {
        backgroundColor = withOpacity(this.state.originalBackGroundColor, 0.8);
      }
    }
    var buttonStyle = {
      backgroundColor: backgroundColor,
      borderColor: borderColor,
      borderWidth: borderWidth,
      borderRadius: 10
    };
    var touchableStyle = {
      borderRadius: style ? StyleSheet.flatten(style).borderRadius || 10 : 10,
      width: '100%'
    };
    var textStyle = {
      color: textColor,
      fontFamily: "Montserrat",
      fontWeight: "bold",
      fontSize: 13,
      textTransform: 'uppercase'
    };
    var elevation = disabled || mode !== "contained" ? 0 : this.state.elevation;
    return /*#__PURE__*/React.createElement(Hoverable, {
      onHoverIn: function () {
        return _this.setState({
          hovering: true
        });
      },
      onHoverOut: function () {
        return _this.setState({
          hovering: false
        });
      }
    }, /*#__PURE__*/React.createElement(Surface, __assign({
      nativeID: rest.idTag1,
      testID: "uiButton"
    }, rest, {
      style: [compacted && styles.compact, elevation, buttonStyle, style, this.state.hovering && {
        cursor: 'pointer'
      }]
    }), /*#__PURE__*/React.createElement(PnmacTouchableRipple, {
      borderless: true,
      delayPressIn: 0,
      onPress: loading ? function () {
        return null;
      } : onPress,
      onPressIn: this.handlePressIn,
      onPressOut: this.handlePressOut,
      accessibilityLabel: accessibilityLabel,
      accessibilityTraits: disabled ? ["button", "disabled"] : "button",
      accessibilityComponentType: "button",
      accessibilityStates: disabled ? ["disabled"] : [],
      disabled: disabled,
      rippleColor: rippleColor,
      style: touchableStyle,
      testID: "uiButton_".concat(testID),
      nativeID: rest.idTag2
    }, /*#__PURE__*/React.createElement(View, {
      accessible: true,
      accessibilityLabel: 'Button ' + (typeof children === 'string' ? children : accessibilityLabel),
      style: [styles.content, contentStyle],
      nativeID: rest.idTag3
    }, icon && loading !== true ? (/*#__PURE__*/React.createElement(View, {
      style: __assign(__assign({}, styles.icon), iconStyles),
      nativeID: rest.idTag4
    }, /*#__PURE__*/React.createElement(Icon, {
      source: icon,
      size: iconSize || 16,
      color: textColor,
      nativeID: rest.idTag5
    }))) : null, loading ? (/*#__PURE__*/React.createElement(View, {
      accessible: true,
      accessibilityLabel: 'Loading',
      style: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        justifyContent: 'center',
        alignItems: 'center'
      }
    }, /*#__PURE__*/React.createElement(ActivityIndicator, {
      size: 16,
      color: textColor,
      nativeID: rest.idTag6
    }))) : null, /*#__PURE__*/React.createElement(View, {
      style: {
        opacity: loading ? 0 : 1
      }
    }, typeof children === 'string' ? /*#__PURE__*/React.createElement(PnmacText, {
      numberOfLines: 1,
      style: [styles.label, compacted && styles.compactLabel, textStyle],
      nativeID: rest.idTag7
    }, children) : /*#__PURE__*/React.createElement(View, {
      style: [styles.labelView, labelStyle]
    }, !loading && children))))));
  };
  PnmacUIButton.defaultProps = {
    mode: "text",
    uppercase: false,
    hovering: false,
    style: {}
  };
  return PnmacUIButton;
}(React.Component);
export default PnmacUIButton;